import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box } from '@primer/react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const CustomVideoPlayer = makeShortcode("CustomVideoPlayer");
const Caption = makeShortcode("Caption");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`This documentation provides an overview of navigation design patterns within Primer. For more granular guidance about the components and insights into their implementation, reference their respective guidelines pages.`}</p>
    <h2>{`Best practices`}</h2>
    <p>{`Prioritize clarity and simplicity to facilitate seamless user interaction.`}</p>
    <h3>{`Clear user orientation`}</h3>
    <p>{`Users should always have a clear understanding of their location within the application. Intuitive navigational cues minimize ambiguity.`}</p>
    <h3>{`Streamlined choices`}</h3>
    <p>{`Minimize the number of navigational elements while ensuring that the most essential items are available. This balance can help prevent users from feeling overwhelmed with too many options, but also ensure that they can easily find what they need.`}</p>
    <h3>{`Contextual transitions`}</h3>
    <p>{`Strive to maintain a balance between contextual clarity and minimal interruption. While transitions such as page refreshes or showing a dialog are useful for indicating context switches, excessive transitions can lead to unnecessary cognitive load.`}</p>
    <h2>{`Contextual shifts and transitions`}</h2>
    <h3>{`Entirely new page`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`This is the default behavior of clicking a link or submitting a form. The URL changes and the requested page loads.`}</p>
        <p>{`In GitHub's single-page app architecture, this behavior is emulated without a traditional page reload.`}</p>
        <p>{`If you're navigating from a parent page to a child page, the child page should have a clear way to navigate back to the parent page. This is often done with `}<a parentName="p" {...{
            "href": "/components/breadcrumbs"
          }}>{`breadcrumbs`}</a>{` or a "back" button.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/6d8008b5-bc5a-4f21-ba14-ac57550b2de3" mdxType="CustomVideoPlayer" />
    </Box>
    <p>{`For example, when adding a new Codespace secret, the user is taken to a new page to fill out the secret details. There is a breadcrumb at the top of the page letting them go back to the Codespace settings page.`}</p>
    <p>{`Be mindful of data loss when a user is navigating away from a page where they have unsaved changes. See the `}<a parentName="p" {...{
        "href": "/ui-patterns/saving"
      }}>{`saving`}</a>{` guidelines for more info on handling unsaved changes.`}</p>
    <h3>{`Same page, different state`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Example: user activated a different state of their current page. `}</p>
        <p>{`When using this kind of navigation, strive to avoid full page reloads when feasible. This could help maintain a fast and smooth user experience.`}</p>
        <p>{`The URL may or may not change.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/bbd4675d-546f-4f7d-9736-8b6419ed54c5" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`New spot on the same page`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Example: User navigated to a different section or moved focus to a specific element within the same page. `}</p>
        <p>{`The page content is not changed. This behavior is commonly used for scrolling to new sections or focusing on input fields.`}</p>
        <p>{`The URL should change if the section scrolled to is a page anchor.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/c4e4672a-11a6-4444-aaa6-f8f2b032f11b" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`Moving into a dialog`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Opening a dialog brings the user into a new context, but can feel "lighter" than going to a whole new page. Focus is moved into the dialog, and the user can't interact with the rest of the page without dismissing the dialog.`}</p>
        <p>{`The URL may or may not change when opening a dialog.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/ec911107-016e-4d64-9d08-c235b19a32cb" mdxType="CustomVideoPlayer" />
    </Box>
    <h2>{`Patterns and components`}</h2>
    <h3>{`Nav list`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`A vertical list of links that can be used to implement a parent-detail navigation pattern. Users can navigate between different child views without leaving the parent page. It's often used in the sidebar of a split page layout.`}</p>
        <p>{`Activating a nav list item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/nav-list"
          }}>{`Nav list component guidelines`}</a></p>
      </Box>
      <img width="456" alt="A nav list component" src="https://github.com/primer/design/assets/2313998/ce7949ad-d9d7-4f07-a531-73c074ff6b54" />
    </Box>
    <h3>{`Tree view`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`A tree view isn't semantically a form of navigation: it's for activating an option from a hierarchical list. However, it's often used to implement a parent-detail navigation pattern. It's often used in the sidebar of a split page layout.`}</p>
        <p>{`Activating a tree view item may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/tree-view"
          }}>{`Tree view component guidelines`}</a></p>
      </Box>
      <img width="456" alt="A tree view component" src="https://github.com/primer/design/assets/2313998/ac179492-b53e-4211-a61a-0c4605808c64" />
    </Box>
    <h3>{`Tabs`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Tabs allow users to switch between related views within a consistent context, enhancing navigation efficiency and organization.`}</p>
        <p>{`Use the `}<a parentName="p" {...{
            "href": "/components/underline-nav"
          }}>{`underline nav`}</a>{` component when activating a tab changes the URL.`}</p>
        <p>{`Use the `}<a parentName="p" {...{
            "href": "/components/underline-panels"
          }}>{`underline panels`}</a>{` component when activating a tab changes the content in the same page or context without changing the URL.`}</p>
        <p>{`Activating a tab may or may not change the URL, but you can't mix tabs that change the URL with tabs just switch the visible tab panel.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/underline-nav"
          }}>{`Underline nav component guidelines`}</a></p>
        <p><a parentName="p" {...{
            "href": "/components/underline-panels"
          }}>{`Underline panel component guidelines`}</a></p>
      </Box>
      <img width="456" alt="An underline nav component highlighted on a repository page" src="https://github.com/primer/design/assets/2313998/f287c9dc-fb4c-401e-aa2e-5ca7e30ea27d" />
    </Box>
    <h3>{`Action menu`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`An action menu is not semantically a form of navigation: it's for activating an option from a list. However, it may contain some options that open a dialog or change the URL when activated.`}</p>
        <p>{`Action menus are also used to overflow horizontal navigation elements (such as tabs) when the container is too narrow to display all options inline.`}</p>
        <p>{`Activating an action menu item may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/action-menu"
          }}>{`Action menu component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of '+' action menu expanded in global navigation" src="https://github.com/primer/design/assets/2313998/f8efba93-2a4f-43f6-b428-d804b423c737" />
    </Box>
    <h3>{`Breadcrumbs`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Breadcrumbs show the user where they are using a hierarchical list of links. Going from start to end, each link is one level of hierarchy deeper.`}</p>
        <p>{`Activating a breadcrumb item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/breadcrumbs"
          }}>{`Breadcrumbs component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of files page breadcrumb" src="https://github.com/primer/design/assets/2313998/2a8aa05b-b406-44f8-9f8d-5f621fcad221" />
    </Box>
    <h3>{`Pagination`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Pagination is used to sequentially navigate long collections of content that's been broken into smaller chunks.`}</p>
        <p>{`Activating a pagination item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/pagination"
          }}>{`Pagination component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of issues page pagination" src="https://github.com/primer/design/assets/2313998/7052c7d9-1e7a-41c1-8854-f85630c92df2" />
    </Box>
    <h3>{`Segmented control`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`While primarily used to make a selection from a short list, segmented controls can be employed as navigational components for `}<strong parentName="p">{`filtering`}</strong>{`, `}<strong parentName="p">{`sorting`}</strong>{`, or `}<strong parentName="p">{`viewing content in a different format`}</strong>{`.`}</p>
        <p>{`When being used for navigation, activating a segmented control option may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/segmented-control"
          }}>{`Segmented control component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of code/blame/preview on .md files" src="https://github.com/primer/design/assets/2313998/0b1e308d-a351-4457-8214-d863d8f76635" />
    </Box>
    <h2>{`Choosing a pattern for tabbed navigation`}</h2>
    <p>{`When designing an interface with tabbed navigation, first reach for `}<a parentName="p" {...{
        "href": "/components/underline-nav"
      }}>{`underline nav`}</a>{` (when clicking the tab changes the URL) or `}<a parentName="p" {...{
        "href": "/components/underline-panels"
      }}>{`underline panels`}</a>{` (when clicking the tab just toggles tab panel visibility).`}</p>
    <p>{`If these components don't fit your use-case, you may use a `}<a parentName="p" {...{
        "href": "/components/segmented-control#to-toggle-content-visibility"
      }}>{`segmented control`}</a>{` where the segments are links or `}<inlineCode parentName="p">{`role="tab"`}</inlineCode>{`.`}</p>
    <img width="960" alt="Decision tree for switching views using a tabbed interface. The tree begins with the statement 'I want to switch views using a tabbed interface' and splits into several paths: If the format of the content changes (e.g., changing between 'List' and 'Grid' views), and the selection switches content visibility like tabs, use a SegmentedControl that is a tablist, with each view as its own tab panel. If not, use a SegmentedControl with link segments. If the items are mutually exclusive options that sort content on the page, and the URL changes when you activate an item, use a SegmentedControl with button segments. If not, use a SegmentedControl with link segments. If the items are mutually exclusive options that filter content on the page, and there is an option like 'All' that removes the filter, use UnderlinePanels. If not, and the URL changes when you activate an item, use UnderlinePanels. If not, use UnderlineNav." src="https://github.com/primer/design/assets/2313998/21c827ba-9e08-4267-a7a9-a9ce7a72cd30" />
    <h2>{`Layout`}</h2>
    <p>{`Layout is used to structure your content in a way that reflects the hierarchy of the navigation within the app. Navigational elements should be laid out in close proximity to the content they affect.`}</p>
    <p>{`For example, pagination should go directly below the collection it navigates.`}</p>
    <h3>{`Layouts for navigating in the same context`}</h3>
    <h4>{`Parent-detail split page layout`}</h4>
    <img width="960" alt="Screenshot of a repo's security advisories page with the sidebar and main areas highlighted" src="https://github.com/primer/design/assets/2313998/7d8bafeb-f971-45d2-ac0e-7850d7f81912" />
    <p>{`A "parent" sidebar that affects "detail" content. The "parent" navigation remains on screen while the user navigates between different "detail" views.`}</p>
    <h4>{`Tabbed interfaces`}</h4>
    <img width="960" alt="Screenshot of a repo's security advisories page with the tabs and tab panel areas highlighted" src="https://github.com/primer/design/assets/2313998/f7fb7251-f9bf-4d58-b6ec-16b66f3959da" />
    <p>{`Tabbed interfaces enable seamless navigation between different subpages within a unified context. Tabs go directly above the content they affect.`}</p>
    <h3>{`Headings and hierarchical page sections`}</h3>
    <img width="960" alt="Wireframe of a settings page with multiple nested sections" src="https://github.com/primer/design/assets/2313998/e924678e-1bf6-4317-8953-53d3cd6da3e3" />
    <p>{`A `}<a parentName="p" {...{
        "href": "/components/page-header"
      }}>{`page header`}</a>{` is the title area at the top of the main content region that shows the user where they are. It may also to contain navigational elements like breadcrumbs or a link to go up a level of hierarchy.`}</p>
    <p>{`Use section headings to show the user where they are in the context of the page. These headings should be styled to reflect their hierarchy within the page. Page section headings should be marked up with the appropriate heading level (`}<inlineCode parentName="p">{`h3`}</inlineCode>{`–`}<inlineCode parentName="p">{`h6`}</inlineCode>{`).`}</p>
    <p>{`See our `}<a parentName="p" {...{
        "href": "/primitives/storybook/?path=/story/typography-functional--overview"
      }}>{`typographic style options`}</a>{` for heading styles.`}</p>
    <h2>{`Responsive sidebar navigation patterns`}</h2>
    <p>{`On wide viewports, the sidebar is always visible. The way we show sidebar controls on narrow viewports depends on how the sidebar affects the main content area.`}</p>
    <h3>{`Sidebar as index of links to detail pages`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 2
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <img alt="Wide repo settings page" src="https://github.com/primer/design/assets/2313998/70299dbf-306d-4791-be02-e14da92e7dd4" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Wide viewports</Caption>
      </Box>
      <Box flex={1} mdxType="Box">
        <img alt="Index page next to detail page" src="https://github.com/primer/design/assets/2313998/0a36f76d-17aa-4982-b67b-423b1374ec00" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Narrow viewports</Caption>
      </Box>
    </Box>
    <p>{`On narrow viewports, the sidebar is not visible if the user is navigated to `}<inlineCode parentName="p">{`/{parent}/{detail-page}`}</inlineCode>{`. To see the detail page links, users must go back to `}<inlineCode parentName="p">{`/{parent}`}</inlineCode>{`.`}</p>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3,
      my: 4
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`If the flow only allows users to go one level deep (for example, `}<inlineCode parentName="p">{`/{parent}/{detail-page}`}</inlineCode>{`), show a link to navigate back to the parent route to see the sidebar as an index page.`}</p>
      </Box>
      <Box flex={1} sx={{
        maxWidth: 456
      }} mdxType="Box">
        <img alt="Close-up of header with back arrow" src="https://github.com/primer/design/assets/2313998/25f52e90-5c4f-405f-92ce-341e8d5d6032" />
      </Box>
    </Box>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3,
      my: 4
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`If the flow allows the user to go deeper than the detail page (for example, `}<inlineCode parentName="p">{`{parent}/{detail-page}/{child-route}`}</inlineCode>{`), use a `}<a parentName="p" {...{
            "href": "/components/breadcrumbs"
          }}>{`breadcrumb`}</a>{` to allow users to navigate anywhere in the hierarchy. The breadcrumbs may also be visible on wide viewports, but it's not really needed since the user can use the link in the sidebar to go up a level.`}</p>
      </Box>
      <Box flex={1} sx={{
        maxWidth: 456
      }} mdxType="Box">
        <img alt="Close-up of header with breadcrumbs" src="https://github.com/primer/design/assets/2313998/3d203719-dfba-4d3d-9fb5-c4f07598a4c1" />
      </Box>
    </Box>
    <Box sx={{
      mb: 6
    }} mdxType="Box">
      <p>{`You can see examples of this pattern implemented using Primer React components in Storybook:`}</p>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/react/storybook?path=/story/components-pagelayout-examples--parent-detail"
          }}>{`PageLayout / Examples / Parent-detail (w/ back arrow)`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/react/storybook?path=/story/components-pagelayout-examples--parent-detail-breadcrumb"
          }}>{`PageLayout / Examples / Parent-detail (w/ breadcrumbs)`}</a></li>
      </ul>
    </Box>
    <h3>{`Sidebar as set of filter options`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3,
      my: 4
    }} mdxType="Box">
      <Box flex={2} mdxType="Box">
        <img alt="Wide marketplace redesign page" src="https://github.com/primer/design/assets/2313998/74d3dba1-58c9-4912-bcb0-e5bd095c5899" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Wide viewports</Caption>
      </Box>
      <Box flex={1} mdxType="Box">
        <img alt="Marketplace page on small screen with action menu open" src="https://github.com/primer/design/assets/2313998/b2d5fbd6-77e5-4d13-8f73-68217aaf907a" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Narrow viewports</Caption>
      </Box>
    </Box>
    <p>{`On narrow viewports, the sidebar is hidden and filter options become items in a menu. Putting the menu options in a menu saves screen real estate without removing them from the page.`}</p>
    <p>{`You can see examples of this pattern implemented using Primer React components in Storybook:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react/storybook?path=/story/components-pagelayout-examples--filter-action-menu"
        }}>{`PageLayout / Examples / Filters (action menu on narrow)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react/storybook?path=/story/components-pagelayout-examples--filter-bottom-sheet"
        }}>{`PageLayout / Examples / Filters (bottom sheet on narrow)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react/storybook?path=/story/components-pagelayout-examples--filter-bottom-sheet"
        }}>{`PageLayout / Examples / Filters w/ 2 levels (bottom sheet on narrow)`}</a></li>
    </ul>
    <h4>{`Filter menu`}</h4>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3,
      my: 4
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Use an `}<a parentName="p" {...{
            "href": "/components/action-menu"
          }}>{`action menu`}</a>{` for most cases, but use a `}<a parentName="p" {...{
            "href": "/components/dialog#bottom-sheets"
          }}>{`bottom sheet dialog`}</a>{` if there are too many options to comfortably fit in an action menu. There's no hard guideline on the max number of items for an action menu but you can use 15 as the max if you're unsure.`}</p>
        <h5>{`Menu trigger options`}</h5>
        <p>{`By default, add a new button to the main content area header. Activating this button opens the menu of filter options.`}</p>
      </Box>
      <img width="456" alt="Marketplace page on small screen with action menu open" src="https://github.com/primer/design/assets/2313998/263cc002-0e1f-4413-bb5b-b760385581a5" />
    </Box>
    <Box sx={{
      mb: 6
    }} mdxType="Box">
      <p>{`Alternatively, you may use the content area's heading as a trigger to open the menu of filter options when activated. This pattern is useful for keeping the page header clean by repurposing the heading as a control instead of adding another button to the header. The heading text will be the same as the currently selected filter, so this pattern works when the names of the filters describe the page content.`}</p>
      <p>{`Since the heading will contain a button, it should be perceivable as an interactive element: it should have styles for interative states (hover, focus, and pressed) and a trailing dropdown triangle to differntiate it from plain text.`}</p>
    </Box>
    <h3>{`Sidebar with a mix of detail page links and filter options`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 2
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <img alt="Wide search page" src="https://github.com/primer/design/assets/2313998/436586c9-0ef8-434c-80e0-efd4a8b76767" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Wide viewports</Caption>
      </Box>
      <Box flex={1} mdxType="Box">
        <img alt="Narrow search index page + Narrow search page with bottom sheet open" src="https://github.com/primer/design/assets/2313998/4bd55169-aefb-4b05-bafc-bbebefbd4761" />
        <Caption role="presentation" color="fg.subtle" mdxType="Caption">Narrow viewports</Caption>
      </Box>
    </Box>
    <p>{`This pattern is just a combination of the two previous patterns described in this section.`}</p>
    <p>{`On narrow viewports, the sidebar is hidden. Filter options become items in a menu, and detail page links are moved to a parent "index" view.`}</p>
    <p>{`You can see an example of this pattern implemented using Primer React components in Storybook: `}<a parentName="p" {...{
        "href": "?path=/story/components-pagelayout-examples--parent-detail-plus-filters"
      }}>{`PageLayout / Examples / Parent-detail + filters (filters as btm sheet on narrow)`}</a></p>
    <h2>{`Accessibility`}</h2>
    <h3>{`Skip links`}</h3>
    <p>{`Facilitate efficient navigation for screen reader users by including skip links to bypass dense redundant content and navigate directly to essential sections.`}</p>
    <p>{`Example for skipping redundant content: there is a skip link near the global navigation that allows users skip over all the global navigation items and go straight to the main content.`}</p>
    <p>{`Example for skipping dense content: there is a skip link near the header of a data table that allows users to skip over the large amount of content often rendered in a data table.`}</p>
    <h3>{`Tab order`}</h3>
    <p>{`Users should be able to tab through the navigation elements in a logical order. The tab order should reflect the visual order of the elements.`}</p>
    <h3>{`Scannable headings`}</h3>
    <p>{`A correct heading structure is critical for enabling users to navigate quickly within a page. Headings are by far the most common navigation technique for screen reader users.`}</p>
    <p>{`Follow our accessibility `}<a parentName="p" {...{
        "href": "/guides/accessibility/headings"
      }}>{`guidelines for headings`}</a>{`.`}</p>
    <h2>{`Related links`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/action-menu"
        }}>{`Action menu`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/breadcrumbs"
        }}>{`Breadcrumbs`}</a>
        {/* markdownlint-disable GH002 "Avoid using generic link text" - "Link" is actually the name of the component */}
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/link"
        }}>{`Link`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/pagination"
        }}>{`Pagination`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/segmented-control"
        }}>{`Segmented control`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/underline-nav"
        }}>{`Underline nav`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/underline-panels"
        }}>{`Underline panels`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/focus-management"
        }}>{`Accessibility - focus management`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/headings"
        }}>{`Accessibility - headings`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/links"
        }}>{`Accessibility - links`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/foundations/layout#page-types"
        }}>{`Responsive layout`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/patterns/saving"
        }}>{`Saving`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      